/* Rekover : l'accueil « corps qui récupère ».
   Un corps d'athlète en verre, muscles brûlants à l'intérieur. Au fil du scroll, la caméra passe de zone en zone
   (mollets, cuisses, dos, nuque, bras) et une onde argentée remonte chaque muscle : il se détend et devient argent miroir.
   Le grand mot REKOVER se fissure puis se répare en même temps.
   Three.js (MIT) + Motion (Framer Motion) pour lire le scroll. Modèle anatomique : BodyParts3D, © DBCLS, CC BY 4.0. */
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

const sec = document.querySelector('[data-rkb]');
if (sec && !sec.dataset.ready) { sec.dataset.ready = '1'; init(); }

function init() {
  const d = document, html = d.documentElement;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = matchMedia('(hover: hover) and (pointer: fine)').matches;
  const M = window.Motion;
  const $ = (s) => sec.querySelector(s), $$ = (s) => [...sec.querySelectorAll(s)];
  const track = $('.rkb__track'), stageEl = $('[data-rkb-stage]');
  const chapters = $$('[data-rkb-ch]'), steps = $$('[data-rkb-steps] li');
  const endEl = $('[data-rkb-end]'), bar = $('[data-rkb-bar]'), hint = $('[data-rkb-hint]');
  const word = $('[data-rkr-word]'), wordWrap = $('.rkr__wordwrap');
  const url = sec.getAttribute('data-model');

  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const sm = (a, b, x) => { const t = clamp((x - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); };
  const lerp = (a, b, t) => a + (b - a) * t;
  const inOut = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  const outQuart = (t) => 1 - Math.pow(1 - t, 4);

  html.classList.add('rk-has-word');

  /* ---------- Progression du scroll ---------- */
  let target = 0, cur = 0;
  const readP = () => { const r = track.getBoundingClientRect(), tot = r.height - innerHeight; target = tot > 0 ? clamp(-r.top / tot, 0, 1) : 0; };
  if (M && M.scroll) M.scroll((v) => { target = v; }, { target: track, offset: ['start start', 'end end'] });
  else addEventListener('scroll', readP, { passive: true });
  readP(); cur = target;

  /* ---------- Textes : chapitres, étapes, fin ---------- */
  let lastEnd = null;
  function ui(p) {
    chapters.forEach((c) => { const on = p >= +c.dataset.from && p < +c.dataset.to; if (on !== c._on) { c._on = on; c.classList.toggle('is-on', on); } });
    steps.forEach((li, i) => { const nx = steps[i + 1]; const on = p >= +li.dataset.from && (!nx || p < +nx.dataset.from); if (on !== li._on) { li._on = on; li.classList.toggle('is-on', on); } });
    const end = p > 0.885;
    if (end !== lastEnd) { lastEnd = end; endEl && endEl.classList.toggle('is-on', end); sec.classList.toggle('is-end', end); }
    if (bar) bar.style.transform = 'scaleX(' + p.toFixed(4) + ')';
    if (hint) hint.style.opacity = (1 - sm(0, 0.015, p)).toFixed(3);
    html.classList.toggle('rk-word-on', track.getBoundingClientRect().bottom > innerHeight * 1.02);
  }

  if (reduce) { sec.classList.add('rkb--static'); ui(1); return; }

  /* ---------- Le mot REKOVER : net, en argent miroir (le reflet qui passe est en CSS) ---------- */
  const base = word && word.querySelector('.rkr__base');
  let wmx = -60, wmy = 30, wtx = -60, wty = 30, lastWo = '', lastM = '';
  if (fine && base) addEventListener('pointermove', (e) => {
    const r = base.getBoundingClientRect(); if (!r.width) return;
    wtx = clamp((e.clientX - r.left) / r.width * 100, -30, 130); wty = clamp((e.clientY - r.top) / r.height * 100, -60, 160);
  }, { passive: true });
  function wordFrame(t, p) {
    // pendant les gros plans, le mot recule en fond pour laisser la place au corps
    const wo = (1 - 0.8 * sm(0.045, 0.085, p) + 0.8 * sm(0.78, 0.86, p)).toFixed(3);
    if (wo !== lastWo) { lastWo = wo; wordWrap.style.opacity = wo; }
    if (!base) return;
    wmx += (wtx - wmx) * 0.08; wmy += (wty - wmy) * 0.08;
    const m = wmx.toFixed(1) + '|' + wmy.toFixed(1);
    if (m !== lastM) { lastM = m; base.style.setProperty('--mx', wmx.toFixed(1) + '%'); base.style.setProperty('--my', wmy.toFixed(1) + '%'); }
  }

  /* ---------- 3D ---------- */
  let renderer = null;
  try { renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' }); }
  catch (e) { renderer = null; }
  if (!renderer || !url) { sec.classList.add('rkb--nobody'); }

  const touch = matchMedia('(pointer: coarse)').matches;
  let scene, camera, muscles = null, chrome = null, ball = null;
  const U = { uEm: { value: 0 }, uC: { value: new THREE.Vector3(0, 0.98, 0) }, uT: { value: 0 }, uHeal: { value: new Array(8).fill(0) }, uLo: { value: new Array(8).fill(0) }, uHi: { value: new Array(8).fill(1.7) } };
  const uChrome = { value: 0 };

  if (renderer) {
    renderer.setPixelRatio(Math.min(devicePixelRatio || 1, touch ? 1.75 : 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.setClearColor(0x000000, 0);
    stageEl.appendChild(renderer.domElement);
    sec.classList.add('rkb--ready');
    scene = new THREE.Scene();
    scene.environment = new THREE.PMREMGenerator(renderer).fromScene(new RoomEnvironment(renderer), 0.03).texture;
    camera = new THREE.PerspectiveCamera(30, 1, 0.05, 30);
    const key = new THREE.DirectionalLight(0xffffff, 1.2); key.position.set(2, 4, 3); scene.add(key);
    const rim = new THREE.DirectionalLight(0xe8eef5, 1.0); rim.position.set(-3, 2, -4); scene.add(rim);

    /* la goutte d'argent du début : le corps en sortira */
    const mBall = new THREE.MeshPhysicalMaterial({ color: 0xeef1f4, metalness: 1, roughness: 0.03, envMapIntensity: 1.3 });
    const uW = { value: 0.12 };
    mBall.onBeforeCompile = (sh) => {
      sh.uniforms.uT = U.uT; sh.uniforms.uW = uW;
      sh.vertexShader = 'uniform float uT;\nuniform float uW;\n' + sh.vertexShader.replace('#include <begin_vertex>',
        '#include <begin_vertex>\nfloat wob = sin(position.y*26.0+uT*2.1)*0.012 + sin(position.x*30.0-uT*1.7)*0.01 + sin(position.z*22.0+uT*1.3)*0.009;\ntransformed += normal * wob * uW;');
    };
    ball = new THREE.Mesh(new THREE.SphereGeometry(0.2, 96, 64), mBall); ball.userData.uW = uW; scene.add(ball);

    if (url) loadModel(url).then((buf) => new Promise((ok, ko) => new GLTFLoader().parse(buf, '', ok, ko))).then((gltf) => {
      gltf.scene.traverse((o) => {
        if (!o.isMesh) return;
        o.geometry.computeVertexNormals();
        if (o.name.indexOf('skin') === 0 || o.parent && o.parent.name === 'skin') skinMesh(o); else muscleMesh(o);
      });
      if (muscles && chrome) { addHead(chrome, gltf.scene); addBelly(chrome, gltf.scene); }
      scene.add(gltf.scene);
      sec.classList.add('rkb--ready');
    }).catch(() => { sec.classList.add('rkb--nobody'); });
  }

  /* Le modèle peut être un .glb, ou un .png qui le transporte (Shopify sur iPad n'accepte que les images) :
     on lit alors les pixels bruts du PNG (sans passer par un canvas, pour garder chaque octet exact). */
  async function loadModel(src) {
    const buf = await (await fetch(src)).arrayBuffer(), u8 = new Uint8Array(buf);
    if (u8[0] !== 0x89 || u8[1] !== 0x50) return buf;
    const dv = new DataView(buf); let o = 8, w = 0, h = 0, ct = 2; const idat = [];
    while (o < u8.length) {
      const len = dv.getUint32(o), type = String.fromCharCode(u8[o + 4], u8[o + 5], u8[o + 6], u8[o + 7]);
      if (type === 'IHDR') { w = dv.getUint32(o + 8); h = dv.getUint32(o + 12); ct = u8[o + 17]; }
      else if (type === 'IDAT') idat.push(u8.subarray(o + 8, o + 8 + len));
      else if (type === 'IEND') break;
      o += 12 + len;
    }
    const z = await new Response(new Blob(idat).stream().pipeThrough(new DecompressionStream('deflate'))).arrayBuffer();
    const src8 = new Uint8Array(z), bpp = ct === 6 ? 4 : 3, stride = w * bpp, out = new Uint8Array(stride * h);
    for (let y = 0; y < h; y++) {
      const f = src8[y * (stride + 1)], row = y * stride, inp = y * (stride + 1) + 1;
      for (let x = 0; x < stride; x++) {
        const a = x >= bpp ? out[row + x - bpp] : 0, b = y ? out[row - stride + x] : 0, c = x >= bpp && y ? out[row - stride + x - bpp] : 0;
        let v = src8[inp + x];
        if (f === 1) v += a; else if (f === 2) v += b; else if (f === 3) v += (a + b) >> 1;
        else if (f === 4) { const p = a + b - c, pa = Math.abs(p - a), pb = Math.abs(p - b), pc = Math.abs(p - c); v += pa <= pb && pa <= pc ? a : pb <= pc ? b : c; }
        out[row + x] = v & 255;
      }
    }
    let data = out;
    if (bpp === 4) { data = new Uint8Array(w * h * 3); for (let i = 0, j = 0; i < out.length; i += 4) { data[j++] = out[i]; data[j++] = out[i + 1]; data[j++] = out[i + 2]; } }
    const n = data[0] | data[1] << 8 | data[2] << 16 | data[3] << 24;
    return data.slice(4, 4 + n).buffer;
  }

  function muscleMesh(o) {
    // hauteur de chaque zone, pour que l'onde argentée la remonte des pieds vers le haut
    const z = o.geometry.getAttribute('_zone'), pos = o.geometry.getAttribute('position');
    const lo = new Array(8).fill(9), hi = new Array(8).fill(-9);
    for (let i = 0; i < z.count; i++) { const k = Math.round(z.getX(i)), y = pos.getY(i); if (y < lo[k]) lo[k] = y; if (y > hi[k]) hi[k] = y; }
    for (let k = 0; k < 8; k++) { if (lo[k] < 9) { U.uLo.value[k] = lo[k]; U.uHi.value[k] = hi[k]; } }
    const mat = new THREE.MeshPhysicalMaterial({ color: 0xf2f4f6, metalness: 1, roughness: 0.07, clearcoat: 1, clearcoatRoughness: 0.05, envMapIntensity: 1.35 });
    mat.onBeforeCompile = (sh) => {
      Object.assign(sh.uniforms, U);
      sh.vertexShader = 'uniform float uEm;\nuniform vec3 uC;\nattribute float _zone;\nvarying float vZone;\nvarying vec3 vP;\n' + sh.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\nvZone = _zone; vP = position;\nfloat dC = length(position - uC);\nfloat em = clamp((uEm * 1.7 - dC * 0.9) / 0.35, 0.0, 1.0);\nem = 1.0 - pow(1.0 - em, 3.0);\ntransformed = uC + (transformed - uC) * em;');
      sh.fragmentShader = 'uniform float uT;\nuniform float uHeal[8];\nuniform float uLo[8];\nuniform float uHi[8];\nvarying float vZone;\nvarying vec3 vP;\n' + sh.fragmentShader.replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
        int zi = int(vZone + 0.5);
        float h = uHeal[zi];
        float front = mix(uLo[zi] - 0.05, uHi[zi] + 0.05, h);
        float healed = smoothstep(-0.025, 0.025, front - vP.y);
        float heat = (zi == 0 ? 0.5 : 1.0) * (1.0 - healed);
        float pulse = 0.76 + 0.24 * sin(uT * 3.4 - vP.y * 4.0 + float(zi) * 1.7);
        vec3 ember = vec3(0.55, 0.045, 0.03) * pulse;
        diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.08, 0.012, 0.012), heat * 0.9);
        totalEmissiveRadiance += ember * heat * 0.7;
        float edge = exp(-pow((front - vP.y) / 0.02, 2.0)) * step(0.001, h) * step(h, 0.999);
        totalEmissiveRadiance += vec3(0.9, 0.95, 1.0) * edge * 0.9;`);
    };
    o.material = mat; o.renderOrder = 1; muscles = o;
  }

  /* la tête : le modèle anatomique n'a pas de muscles du crâne, on reprend la peau du haut de la tête : rouge au début, elle passe à l'argent après la nuque */
  let head = null;
  function addHead(skin, parent) {
    const g = skin.geometry, pos = g.getAttribute('position'), idx = g.index, keep = [];
    for (let i = 0; i < idx.count; i += 3) {
      const a = idx.getX(i), b = idx.getX(i + 1), c = idx.getX(i + 2);
      if (pos.getY(a) > 1.5 && pos.getY(b) > 1.5 && pos.getY(c) > 1.5) keep.push(a, b, c);
    }
    const hg = new THREE.BufferGeometry();
    hg.setAttribute('position', pos); hg.setAttribute('normal', g.getAttribute('normal'));
    hg.setAttribute('_zone', new THREE.BufferAttribute(new Float32Array(pos.count).fill(7), 1));
    hg.setIndex(keep);
    let lo = 9, hi = -9; for (const i of keep) { const y = pos.getY(i); if (y < lo) lo = y; if (y > hi) hi = y; }
    U.uLo.value[7] = lo; U.uHi.value[7] = hi;
    head = new THREE.Mesh(hg, muscles.material); head.renderOrder = 1; parent.add(head);
  }

  /* le ventre : absent du modèle anatomique, on le modèle à part : une coque fine et plate qui suit la peau juste en dessous,
     ses bords passent doucement sous la poitrine, les côtes et le bassin */
  let belly = null;
  function addBelly(skin, parent) {
    const pos = skin.geometry.getAttribute('position');
    const NX = 41, NY = 51, x0 = -0.2, x1 = 0.2, y0 = 0.82, y1 = 1.3, dx = (x1 - x0) / (NX - 1), dy = (y1 - y0) / (NY - 1);
    const Hm = new Float32Array(NX * NY).fill(-1), fix = new Uint8Array(NX * NY);
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
      if (z < 0 || x < x0 || x > x1 || y < y0 || y > y1) continue;
      const k = Math.round((y - y0) / dy) * NX + Math.round((x - x0) / dx);
      if (z > Hm[k]) Hm[k] = z;
    }
    for (let k = 0; k < Hm.length; k++) { if (Hm[k] > 0) fix[k] = 1; else Hm[k] = 0.06; }
    for (let it = 0; it < 300; it++) for (let Y = 1; Y < NY - 1; Y++) for (let X = 1; X < NX - 1; X++) {
      const k = Y * NX + X; if (!fix[k]) Hm[k] = 0.25 * (Hm[k - 1] + Hm[k + 1] + Hm[k - NX] + Hm[k + NX]);
    }
    for (let it = 0; it < 4; it++) { const c = Hm.slice(); for (let Y = 1; Y < NY - 1; Y++) for (let X = 1; X < NX - 1; X++) { const k = Y * NX + X; Hm[k] = (4 * c[k] + c[k - 1] + c[k + 1] + c[k - NX] + c[k + NX]) / 8; } }
    const hAt = (x, y) => {
      const fx = clamp((x - x0) / dx, 0, NX - 1.001), fy = clamp((y - y0) / dy, 0, NY - 1.001), X = Math.floor(fx), Y = Math.floor(fy), u = fx - X, v = fy - Y, k = Y * NX + X;
      return lerp(lerp(Hm[k], Hm[k + 1], u), lerp(Hm[k + NX], Hm[k + NX + 1], u), v);
    };
    const RINGS = 64, SEG = 180, cy = 1.07, P = [], I = [];
    // relief des abdominaux : ligne blanche au centre, 3 intersections (6 carrés), bords des grands droits, nombril, obliques et V du bas
    const g2 = (v, w) => Math.exp(-(v * v) / (w * w));
    const relief = (x, y) => {
      const ax = Math.abs(x), yy = y - ax * 0.1;
      const inR = (1 - sm(0.07, 0.085, ax)) * sm(0.9, 0.95, y) * (1 - sm(1.19, 1.22, y));
      let r = 0;
      // chaque carré bombe : arrondi en largeur et en hauteur, séparé par la ligne blanche et les intersections
      const B = [0.94, 1.01, 1.08, 1.15, 1.205];
      let by = 0;
      for (let k = 0; k < 4; k++) if (yy >= B[k] && yy < B[k + 1]) by = Math.sin(Math.PI * (yy - B[k]) / (B[k + 1] - B[k]));
      const bx = Math.sin(Math.PI * Math.min(1, ax / 0.078));
      r += 0.0045 * inR * Math.pow(bx, 0.6) * Math.pow(by, 0.6);
      r -= 0.0025 * g2(x, 0.007) * sm(0.93, 0.97, y) * (1 - sm(1.18, 1.21, y));
      r -= 0.0022 * (g2(yy - 1.15, 0.008) + g2(yy - 1.08, 0.008) + g2(yy - 1.01, 0.008)) * (1 - sm(0.06, 0.08, ax));
      r -= 0.003 * g2(ax - 0.082, 0.008) * sm(0.92, 0.97, y) * (1 - sm(1.16, 1.21, y));
      const nd = Math.hypot(x / 1.0, (y - 0.982) / 1.3);
      r -= 0.009 * g2(nd, 0.0075); r += 0.0018 * g2(nd - 0.011, 0.004);
      r += 0.0025 * sm(0.085, 0.1, ax) * (0.7 + 0.3 * Math.sin((y + ax * 0.9) * 140));
      const vline = (ax - 0.03) - (y - 0.9) * 0.9;
      r -= 0.0035 * g2(vline, 0.006) * (1 - sm(0.98, 1.0, y)) * sm(0.03, 0.05, ax);
      return r;
    };
    P.push(0, cy, hAt(0, cy) - 0.018 + relief(0, cy));
    for (let r = 1; r <= RINGS; r++) {
      const t = r / RINGS;
      for (let s2 = 0; s2 < SEG; s2++) {
        const a = s2 / SEG * Math.PI * 2, ca = Math.cos(a), sa = Math.sin(a);
        const sy = Math.sign(sa) * Math.pow(Math.abs(sa), 0.7), sx = Math.sign(ca) * Math.pow(Math.abs(ca), 0.7);
        const y = cy + sy * 0.15 * t, w = lerp(0.07, 0.115, sm(0.92, 1.17, y));
        const x = sx * w * t;
        P.push(x, y, hAt(x, y) - 0.018 - 0.016 * Math.pow(sm(0.6, 1, t), 2) + relief(x, y) * (1 - sm(0.8, 0.97, t)));
      }
    }
    for (let s2 = 0; s2 < SEG; s2++) I.push(0, 1 + s2, 1 + (s2 + 1) % SEG);
    for (let r = 1; r < RINGS; r++) for (let s2 = 0; s2 < SEG; s2++) {
      const a = 1 + (r - 1) * SEG + s2, b = 1 + (r - 1) * SEG + (s2 + 1) % SEG, c = a + SEG, d = b + SEG;
      I.push(a, c, d, a, d, b);
    }
    const bg = new THREE.BufferGeometry();
    bg.setAttribute('position', new THREE.Float32BufferAttribute(P, 3)); bg.setIndex(I);
    bg.setAttribute('_zone', new THREE.BufferAttribute(new Float32Array(P.length / 3).fill(6), 1));
    bg.computeVertexNormals();
    U.uLo.value[6] = Math.min(U.uLo.value[6], cy - 0.15);
    belly = new THREE.Mesh(bg, muscles.material); belly.renderOrder = 1; parent.add(belly);
  }

  function skinMesh(o) {
    // à la fin, une peau d'argent poli recouvre le corps (façon Surfer d'Argent)
    o.material = new THREE.MeshPhysicalMaterial({ color: 0xf2f4f6, metalness: 1, roughness: 0.05, clearcoat: 1, clearcoatRoughness: 0.04, envMapIntensity: 1.35, transparent: true, opacity: 0, depthWrite: false });
    o.renderOrder = 2; o.visible = false; chrome = o;
  }

  /* trajet de la caméra : [p, angle autour du corps, hauteur visée, distance, décalage latéral] */
  const KEYS = [
    [0.00, 0.00, 0.95, 4.2, 0], [0.07, 0.08, 0.93, 4.0, 0],
    [0.12, 2.8, 0.45, 2.3, 0], [0.21, 3.0, 0.47, 2.2, 0],
    [0.26, 2.9, 0.85, 2.6, 0], [0.35, 3.05, 0.87, 2.5, 0],
    [0.40, 3.14, 1.15, 2.6, 0], [0.50, 3.22, 1.17, 2.5, 0],
    [0.55, 3.3, 1.38, 2.25, 0], [0.64, 3.4, 1.4, 2.15, 0],
    [0.69, 5.95, 1.15, 2.8, 0], [0.76, 6.12, 1.05, 3.3, 0],
    [0.86, 6.28, 0.9, 4.5, -0.32], [1.00, 6.4, 0.9, 4.7, -0.42]
  ];
  function camAt(p) {
    let i = 0; while (i < KEYS.length - 2 && p > KEYS[i + 1][0]) i++;
    const a = KEYS[i], b = KEYS[i + 1], t = inOut(clamp((p - a[0]) / (b[0] - a[0]), 0, 1));
    return [lerp(a[1], b[1], t), lerp(a[2], b[2], t), lerp(a[3], b[3], t), lerp(a[4], b[4], t)];
  }

  /* calendrier de la récupération, zone par zone */
  function healAt(p) {
    const h = U.uHeal.value;
    h[1] = sm(0.15, 0.205, p);                // mollets
    h[2] = sm(0.29, 0.345, p);                // cuisses, fessiers
    h[3] = sm(0.43, 0.49, p);                 // dos
    h[4] = sm(0.57, 0.63, p);                 // nuque, épaules
    h[5] = sm(0.67, 0.74, p);                 // bras
    h[6] = sm(0.67, 0.74, p);                 // poitrine
    h[0] = sm(0.77, 0.85, p);                 // le reste, d'un coup
    h[7] = sm(0.6, 0.66, p);                  // la tête, juste après la nuque
    uChrome.value = sm(0.8, 0.9, p);
    U.uEm.value = sm(0.02, 0.095, p);
  }

  let W = 1, H = 1;
  function resize() {
    W = stageEl.clientWidth; H = stageEl.clientHeight;
    if (renderer) { renderer.setSize(W, H, false); camera.aspect = W / H; camera.updateProjectionMatrix(); }
  }
  resize(); addEventListener('resize', resize);

  let mx = 0, my = 0, tmx = 0, tmy = 0;
  if (fine) addEventListener('pointermove', (e) => { tmx = e.clientX / innerWidth * 2 - 1; tmy = e.clientY / innerHeight * 2 - 1; }, { passive: true });

  let visible = true;
  new IntersectionObserver((e) => { visible = e[0].isIntersecting; }).observe(track);
  let tPrev = performance.now();
  const look = new THREE.Vector3();
  (function loop(now) {
    requestAnimationFrame(loop);
    const dt = Math.min(0.1, (now - tPrev) / 1000); tPrev = now;
    if (!visible) return;
    cur += (target - cur) * (1 - Math.pow(1 - 0.09, dt * 60));
    if (Math.abs(target - cur) < 0.00005) cur = target;
    const t = now / 1000;
    ui(cur);
    wordFrame(t, cur);
    if (!renderer) return;
    U.uT.value = t;
    healAt(cur);
    if (chrome) {
      const c = uChrome.value; chrome.visible = c > 0.01;
      chrome.material.opacity = c; const solid = c > 0.985;
      if (chrome.material.transparent === solid) { chrome.material.transparent = !solid; chrome.material.depthWrite = solid; chrome.material.needsUpdate = true; }
    }
    if (muscles) muscles.visible = U.uEm.value > 0.002;
    if (head) head.visible = muscles.visible && uChrome.value < 0.985;
    if (belly) belly.visible = muscles.visible && uChrome.value < 0.985;
    // la goutte : elle flotte, se gonfle, puis s'ouvre et le corps en sort
    const bs = (1 + 0.3 * sm(0.0, 0.03, cur)) * (1 - sm(0.03, 0.075, cur));
    ball.visible = bs > 0.002;
    ball.position.set(0, 0.98 + Math.sin(t * 1.3) * 0.015 * (1 - sm(0, 0.03, cur)), 0);
    ball.scale.setScalar(Math.max(bs, 0.0001)); ball.rotation.y = t * 0.3;
    ball.userData.uW.value = 0.12 + 1.4 * sm(0.012, 0.065, cur);
    mx += (tmx - mx) * 0.04; my += (tmy - my) * 0.04;
    const narrow = W < 750 || H > W * 1.15;
    let [az, ty, dist, side] = camAt(cur);
    if (narrow) { dist *= dist < 3.4 ? 1.25 : 1.05; side = 0; }
    az += mx * 0.08;
    ty -= my * 0.03;
    const sway = Math.sin(t * 0.3) * 0.02;
    camera.position.set(Math.sin(az + sway) * dist, ty + 0.05 + (dist > 3 ? 0.08 : 0), Math.cos(az + sway) * dist);
    look.set(Math.cos(az) * side, ty, -Math.sin(az) * side);
    camera.lookAt(look);
    renderer.render(scene, camera);
  })(performance.now());
}
